import { type GridCell, GridCellKind, type Item } from '@glideapps/glide-data-grid'
import { styled, useTheme } from '@mui/material/styles'
import CardMenu from '@ui/CardMenu'
import type { MouseEvent, ReactElement, ReactNode } from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { getPillColor, type PillItem } from '../customCells/MultiPillCell'
import { type DotStatus, getDotColor } from '../customCells/StatusCell'
import type { AvailableColumn } from '../types'
import type { RowActionsMenuItem } from './RowActions'

/**
 * Phone-width replacement for the canvas datagrid: one card per row, first
 * column as the card title, the rest as label/value pairs. Shared by every
 * list page, so all lists degrade to the same card shape.
 */

const List = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
}))

const CardRow = styled('a')(({ theme }) => ({
  '&:active': {
    background: theme.palette.action?.hover ?? 'transparent',
  },
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  color: 'inherit',
  cursor: 'pointer',
  display: 'block',
  padding: theme.spacing(1.5, 2),
  textDecoration: 'none',
}))

const CardTop = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'space-between',
}))

const CardTitle = styled('span')(({ theme }) => ({
  ...theme.typography.subtitle2,
  fontWeight: 700,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

const Fields = styled('div')(({ theme }) => ({
  columnGap: theme.spacing(2),
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  marginTop: theme.spacing(1),
  rowGap: theme.spacing(1),
}))

const Field = styled('div')({
  minWidth: 0,
})

const FieldLabel = styled('div')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}))

const FieldValue = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  overflowWrap: 'anywhere',
}))

const Pill = styled('span', {
  shouldForwardProp: (prop) => prop !== 'base' && prop !== 'borderColor' && prop !== 'textColor',
})<{ base: string; borderColor: string; textColor: string }>(
  ({ base, borderColor, textColor, theme }) => ({
    ...theme.typography.caption,
    background: base,
    border: `1px solid ${borderColor}`,
    borderRadius: 5,
    color: textColor,
    padding: '1px 6px',
    whiteSpace: 'nowrap',
  }),
)

const StatusDot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'dotColor',
})<{ dotColor: string }>(({ dotColor }) => ({
  background: dotColor,
  borderRadius: '50%',
  display: 'inline-block',
  flexShrink: 0,
  height: 8,
  width: 8,
}))

interface CustomCellData {
  kind?: string
  value?: unknown
  status?: DotStatus
  color?: { base: string; border: string; text: string }
  items?: readonly PillItem[]
}

export interface MobileCardListProps {
  availableColumns: readonly AvailableColumn[]
  rows: number
  getCellContent: (item: Item) => GridCell
  menuItems: (index: number) => RowActionsMenuItem[]
  rowAnchor?: (item: Item) => string
  /** SPA navigation for the card links (reads data-react-router-path). */
  onAnchorClick?: (event: MouseEvent<HTMLAnchorElement>) => void
  onRowClick?: (item: Item) => void
}

export const MobileCardList = ({
  availableColumns,
  rows,
  getCellContent,
  menuItems,
  rowAnchor,
  onAnchorClick,
  onRowClick,
}: MobileCardListProps): ReactElement => {
  const theme = useTheme()
  const { locale } = useIntl()
  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }),
    [locale],
  )

  const renderCell = (cell: GridCell): ReactNode => {
    if (cell.kind === GridCellKind.Custom) {
      const data = cell.data as CustomCellData
      switch (data.kind) {
        case 'status-cell':
          return (
            <>
              <StatusDot dotColor={getDotColor(data.status ?? 'default', theme)} />
              {String(data.value ?? '')}
            </>
          )
        case 'auto-tags-cell':
          return data.color ? (
            <Pill
              base={data.color.base}
              borderColor={data.color.border}
              textColor={data.color.text}
            >
              {String(data.value ?? '')}
            </Pill>
          ) : (
            String(data.value ?? '')
          )
        case 'multi-pill-cell':
          return (data.items ?? []).map((item, index) => {
            const color = getPillColor(theme, item)
            return (
              <Pill base={color.base} borderColor={color.border} key={index} textColor={color.text}>
                {item.label}
              </Pill>
            )
          })
        case 'date-cell': {
          const date =
            typeof data.value === 'string' || typeof data.value === 'number'
              ? new Date(data.value)
              : null
          return date && !isNaN(date.getTime()) ? dateFormatter.format(date) : ''
        }

        default:
          return cell.copyData
      }
    }

    if (cell.kind === GridCellKind.Text || cell.kind === GridCellKind.Number) {
      return cell.displayData
    }

    return 'data' in cell ? String(cell.data ?? '') : null
  }

  // The trailing picker filler column carries no data.
  const columns = availableColumns.filter((column) => column.id !== 'empty')

  return (
    <List data-test-id="datagrid-card-list">
      {Array.from({ length: rows }, (_, row) => {
        const rawHref = rowAnchor?.([0, row])
        const href = rawHref ? (rawHref.startsWith('/') ? rawHref : `/${rawHref}`) : undefined
        const rowMenuItems = menuItems(row)
        return (
          <CardRow
            data-react-router-path={href}
            data-test-id="datagrid-card"
            href={href}
            key={row}
            onClick={(event) => {
              if (onRowClick) {
                event.preventDefault()
                onRowClick([0, row])
                return
              }

              if (href) {
                onAnchorClick?.(event)
              }
            }}
          >
            <CardTop>
              <CardTitle>{renderCell(getCellContent([0, row]))}</CardTitle>
              {rowMenuItems.length > 0 ? (
                <span
                  // The menu lives inside the row link — keep taps on it local.
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                >
                  <CardMenu menuItems={rowMenuItems} />
                </span>
              ) : null}
            </CardTop>
            <Fields>
              {columns.slice(1).map((column, index) => {
                const value = renderCell(getCellContent([index + 1, row]))
                return (
                  <Field key={column.id}>
                    <FieldLabel>{column.title}</FieldLabel>
                    <FieldValue>{value}</FieldValue>
                  </Field>
                )
              })}
            </Fields>
          </CardRow>
        )
      })}
    </List>
  )
}

export default MobileCardList
